import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { auth } from '../firebaseConfig';
import { onAuthStateChanged } from 'firebase/auth';
import { readProductPrepRecovery, updateProductPrepRecovery, withCreationLock } from '../utils/productCreationRecovery';
import { getProductVariants } from '../utils/productVariants';
import { addProductToList, subscribeProductListMemberships, updateListSelection } from '../utils/livestreamLists';
import PrepDialog from './PrepDialog';
import './ProductPrepMemberships.css';
export default function ProductPrepMemberships({ country, product }) {
  const [state, setState] = useState({ lists: [], memberships: [], ready: false });
  const [draft, setDraft] = useState(null), [error, setError] = useState(''), [busy, setBusy] = useState(false);
  const saving = useRef(false);
  const [owner, setOwner] = useState(auth.currentUser?.uid || ''), [pendingPrep, setPendingPrep] = useState(null);
  const context = useRef(''); context.current = `${owner}/${country}/${product.id}`;
  useEffect(() => onAuthStateChanged(auth, user => setOwner(user?.uid || '')), []);
  useEffect(() => {
    setState({ lists: [], memberships: [], ready: false }); setDraft(null); setError('');
    return subscribeProductListMemberships(country, product.id, setState, e => { setError(e.message); setState(s => ({ ...s, ready: false })); });
  }, [country, product.id]);
  useEffect(() => {
    const expectedContext = context.current;
    setPendingPrep(null);
    if (owner) {
      try { setPendingPrep(readProductPrepRecovery(owner, country, product.id)); }
      catch (e) { setError(e.message); }
    }
    return () => { if (context.current === expectedContext) context.current = ''; };
  }, [owner, country, product.id]);
  const choose = listId => {
    const item = state.memberships.find(entry => entry.list.id === listId)?.item;
    setDraft({ listId, item, selection: item ? [...item.selectedVariantIds] : getProductVariants(product).filter(v => v.active).map(v => v.id) });
    setError('');
  };
  const act = async work => {
    if (saving.current) return;
    saving.current = true; setBusy(true); setError('');
    try { await work(); } catch (e) { setError(e.message); } finally { saving.current = false; setBusy(false); }
  };
  const save = () => act(async () => {
    if (!draft.listId) throw new Error('請先選擇備播清單');
    if (draft.item) await updateListSelection({ country, listId: draft.listId, item: draft.item, selectedVariantIds: draft.selection });
    else await addProductToList({ listId: draft.listId, country, product, selectedVariantIds: draft.selection });
    setDraft(null);
  });
  const retryPending = () => act(async () => {
    const expectedContext = context.current;
    const assertOwner = () => {
      if (!owner || auth.currentUser?.uid !== owner || context.current !== expectedContext) throw new Error('帳號或商品已切換，待套用備播仍保留');
    };
    assertOwner();
    await withCreationLock(owner, country, async () => {
      let record = readProductPrepRecovery(owner, country, product.id);
      while (record?.choices.length) {
        assertOwner();
        const choice = record.choices[0];
        if (!state.lists.some(list => list.id === choice.listId && list.country === country && !list.archived)) throw new Error('原備播清單已不存在，選擇仍保留，請確認清單');
        await addProductToList({ country, listId: choice.listId, product, selectedVariantIds: choice.selectedVariantIds });
        assertOwner();
        record = updateProductPrepRecovery(owner, country, record, record.choices.slice(1));
        setPendingPrep(record);
      }
    });
  });
  const variants = getProductVariants(product);
  return <section className="product-prep-memberships" aria-label="目前備播群組">
    <div className="product-prep-heading"><h3>目前備播群組</h3><button type="button" disabled={!state.ready || busy} onClick={() => choose(state.lists[0]?.id || '')}>設定備播</button></div>
    {pendingPrep?.uid === owner && auth.currentUser?.uid === owner && pendingPrep?.choices.length > 0 && <div role="status">
      <p>商品已保存，還有 {pendingPrep.choices.length} 個待套用備播；不影響新增下一筆商品。</p>
      <ul>{pendingPrep.choices.map(choice => <li key={choice.listId}>{state.lists.find(list => list.id === choice.listId)?.name || choice.listId}（{choice.selectedVariantIds.length} 個款式）</li>)}</ul>
      <button type="button" disabled={!state.ready || busy || auth.currentUser?.uid !== owner} onClick={retryPending}>重試待套用備播</button>
    </div>}
    {!state.ready && !error && <p>讀取備播清單中…</p>}
    {state.ready && !state.memberships.length && <p>尚未加入備播清單。</p>}
    {state.memberships.map(({ list, item }) => <div className="product-prep-membership" key={list.id}><div><Link to={`/prep/${country}`}>{list.name}</Link><p>{item.selectedVariantIds.length} 個款式</p></div><div><button type="button" disabled={busy || !state.ready} onClick={() => choose(list.id)}>調整款式</button><button type="button" disabled={busy || !state.ready} onClick={() => {
      if (window.confirm(`從「${list.name}」移除此商品？商品、樣品及照片保留。`)) act(() => updateListSelection({ country, listId: list.id, item, remove: true }));
    }}>移除</button></div></div>)}
    {error && !draft && <p role="alert">{error}</p>}
    {draft && <PrepDialog title="設定備播" busy={busy} onClose={() => setDraft(null)}>
      <div className="product-prep-content"><p>{product.name || '未填品名'}</p>
        <label>備播清單<select value={draft.listId} disabled={busy} onChange={e => choose(e.target.value)}><option value="">請選擇清單</option>{state.lists.map(list => <option value={list.id} key={list.id}>{list.name}</option>)}</select></label>
        {!state.lists.length && <p>尚無備播清單。請先到<Link to={`/prep/${country}`}>備播清單管理</Link>新增。</p>}
        <div className="product-prep-actions"><button type="button" disabled={busy} onClick={() => setDraft(d => ({ ...d, selection: variants.filter(v => v.active).map(v => v.id) }))}>全選</button><button type="button" disabled={busy} onClick={() => setDraft(d => ({ ...d, selection: [] }))}>清除選取</button></div>
        {variants.map(variant => <label className="product-prep-choice" key={variant.id}><input type="checkbox" checked={draft.selection.includes(variant.id)} disabled={busy || (!variant.active && !draft.selection.includes(variant.id))} onChange={e => setDraft(d => ({ ...d, selection: e.target.checked ? [...d.selection, variant.id] : d.selection.filter(id => id !== variant.id) }))}/><span>{variant.name}{!variant.active && '（已停用）'}<small>{variant.rawBarcode || '無條碼'}</small></span></label>)}
        {!variants.length && <p>尚無款式。</p>}
        {error && <p role="alert">{error}</p>}
      </div><footer><button type="button" disabled={busy} onClick={() => setDraft(null)}>取消</button><button type="button" className="product-prep-primary" disabled={busy || !draft.listId || !state.ready} onClick={save}>{busy ? '保存中…' : '保存'}</button></footer>
    </PrepDialog>}
  </section>;
}
